// hooks/useFetchDropdowns.js
import { getAllDriver, getAllSupplier, getAllVehicleNumber } from '_services';
import { getBranchListApi } from '_services/dropdownServices/dropdownService';
import { useState, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import StorageService from 'utils/StorageServices';

const useFetchDropdowns = (ownerShip, formLabelValue) => {
  const [fetchloading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // ========== COMMON PARAMETERS ==========
  const commonParams = useMemo(() => ({
    company_id: StorageService.getCompanyId(),
  }), []);

  // ========== GENERIC API CALL FUNCTION ==========
  const handleFetch = useCallback(async (
    apiFunction,
    apiParams,
    dataKey,
    labelKey,
    errorMessage
  ) => {
    setFetchLoading(true);
    setFetchError(null);

    try {
      const res = await apiFunction({
        ...commonParams,
        ...apiParams,
      });

      if (res?.responseCode === 200) {
        const list = res?.data?.[dataKey]?.map((i) => ({
          ...i,
          label: i?.[labelKey],
          value: i?._id,
        })) || [];
        return {
          items: list,
          total: res?.data?.total_record || 0,
        };
      } else {
        throw new Error(res?.responseMessage || errorMessage);
      }
    } catch (err) {
      setFetchError(err.message);
      toast.error(err.message);
      return { items: [], total: 0 };
    } finally {
      setFetchLoading(false);
    }
  }, [commonParams]);

  // ========== BRANCH ==========
  const fetchBranch = useCallback(async (search = '', page = 1, limit = 20) => {
    return handleFetch(
      getBranchListApi,
      {
        firm_id: StorageService.getFirmId(),
        offset: page,
        limit,
        search,
      },
      'branch_list',
      'branch',
      'Failed to fetch branches'
    );
  }, [handleFetch]);

  // ========== VEHICLE ==========
  const fetchVehicle = useCallback(async (search = '', page = 1, limit = 20) => {
    return handleFetch(
      getAllVehicleNumber,
      {
        offset: page,
        limit,
        search,
      },
      'vehicle_list',
      'vehicle_number',
      'Failed to fetch vehicles'
    );
  }, [handleFetch]);

  // ========== DRIVER ==========
  const fetchDriver = useCallback(async (search = '', page = 1, limit = 20) => {
    const firmId = ownerShip === 2
      ? formLabelValue?.branch_id?.firm_id?._id || ""
      : (ownerShip === 1 || ownerShip === 3)
        ? formLabelValue?.vehicle_id?.firm_id?._id || ""
        : "";

    return handleFetch(
      getAllDriver,
      {
        offset: page,
        limit,
        search,
        firm_id: firmId,
        driver_type: ownerShip === 2 ? [2] : [1, 3],
      },
      'driver_list',
      'name',
      'Failed to fetch drivers'
    );
  }, [handleFetch, ownerShip, formLabelValue]);

  // ========== SUPPLIER ==========
  const fetchSupplier = useCallback(async (search = '', page = 1, limit = 20) => {
    return handleFetch(
      getAllSupplier,
      {
        offset: page,
        limit,
        search,
        firm_id: ownerShip === 2 ? formLabelValue?.branch_id?.firm_id?._id || "" : "",
      },
      'supplier_list',
      'name',
      'Failed to fetch suppliers'
    );
  }, [handleFetch, ownerShip, formLabelValue]);

  const fetchDriverForVoucher = useCallback(async (search = '', page = 1, limit = 20) => {

    return handleFetch(
      getAllDriver,
      {
        offset: page,
        limit,
        search,
        firm_id: StorageService?.getFirmId(),
        driver_type: 1,
      },
      'driver_list',
      'name',
      'Failed to fetch drivers'
    );
  }, [handleFetch]);

  return {
    fetchBranch,
    fetchVehicle,
    fetchDriver,
    fetchSupplier,
    fetchloading,
    fetchError,
    fetchDriverForVoucher
  };
};

export default useFetchDropdowns;